/**
 * One section of a song (P5.5): context, then — only when the grant allows —
 * the words, their meaning and vocabulary notes, then the speak-along entry.
 *
 * Every rights-bearing field is rendered through utils/songRights, which
 * re-checks the capability. A section without display_lyrics shows its label
 * and context only, and cannot be practised.
 */
import { speakAlongBlocker, visibleGloss, visibleLines, visibleNotes } from '../../utils/songRights';
import type { SongSectionView, SongView } from '../../store/api/songLearningApi';

export interface SongSectionPanelProps {
  view: SongView;
  section: SongSectionView;
  onSpeakAlong?: (section: SongSectionView) => void;
}

export function SongSectionPanel({ view, section, onSpeakAlong }: SongSectionPanelProps) {
  const lines = visibleLines(view, section);
  const gloss = visibleGloss(view, section);
  const notes = visibleNotes(view, section);
  const blocker = speakAlongBlocker(view, section);

  return (
    <section className="rounded-lg border border-gray-200 p-4 space-y-3" data-testid="song-section" data-section={section.id}>
      <div>
        <h3 className="font-medium text-gray-900">{section.label}</h3>
        <p className="mt-1 text-sm text-gray-600">{section.context}</p>
      </div>

      {lines ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div lang={view.language} data-testid="song-words">
            {lines.map((l, i) => (
              <p key={i} className="text-base text-gray-900">{l}</p>
            ))}
          </div>
          {gloss ? (
            <div data-testid="song-gloss">
              {gloss.map((g, i) => (
                <p key={i} className="text-sm text-gray-500 italic">{g}</p>
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-500" data-testid="song-gloss-withheld">A translation is not available for this song.</p>
          )}
        </div>
      ) : (
        <p className="text-sm text-gray-500" data-testid="song-words-withheld">
          The words of this section cannot be shown.
        </p>
      )}

      {notes && (
        <ul className="list-disc pl-5 text-xs text-gray-600" data-testid="song-notes">
          {notes.map((n, i) => (
            <li key={i}>{n}</li>
          ))}
        </ul>
      )}

      <div className="flex items-center justify-between gap-3">
        <span className="text-xs text-gray-500">{blocker ?? 'Say the lines, then talk about them with your coach.'}</span>
        <button
          type="button"
          className="btn-primary px-4 py-2 text-sm disabled:opacity-50"
          disabled={!!blocker || !onSpeakAlong}
          title={blocker ?? undefined}
          onClick={() => onSpeakAlong?.(section)}
        >
          Speak along
        </button>
      </div>
    </section>
  );
}

export default SongSectionPanel;
